// Vendor imports
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')

const bem = BemEntity('DropZone')

class DropZone extends React.PureComponent {
  constructor(props) {
    super(props)

    this.ref = React.createRef()

    this.state = {
      active: false
    }

    this.onDragEnter = this.onDragEnter.bind(this)
    this.onDragLeave = this.onDragLeave.bind(this)
    this.onDragOver = this.onDragOver.bind(this)
    this.onDrop = this.onDrop.bind(this)
  }

  // React lifecycle methods

  componentDidMount() {
    if (!this.props.disabled) {
      this.registerDropZoneListeners()
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.disabled && !this.props.disabled) {
      this.registerDropZoneListeners()
    }
    if (!prevProps.disabled && this.props.disabled) {
      this.unregisterDropZoneListeners()
    }
    if (prevState.active !== this.state.active) {
      this.props.onActive(this.state.active)
    }
  }

  componentWillUnmount() {
    this.unregisterDropZoneListeners()
  }

  // Event handler helpers

  registerDropZoneListeners() {
    if (typeof window === 'object') { // Browser-only
      document.addEventListener('dragenter', this.onDragEnter)
      document.addEventListener('dragleave', this.onDragLeave)
      document.addEventListener('dragover', this.onDragOver)
      document.addEventListener('drop', this.onDrop)
    }
  }

  unregisterDropZoneListeners() {
    if (typeof window === 'object') { // Browser-only
      document.removeEventListener('dragenter', this.onDragEnter)
      document.removeEventListener('dragleave', this.onDragLeave)
      document.removeEventListener('dragover', this.onDragOver)
      document.removeEventListener('drop', this.onDrop)
    }
  }

  isElementInModal(el) {
    const modalEl = this.ref.current
    if (!modalEl) {
      return false
    }
    let traverseEl = el
    while (traverseEl) {
      if (traverseEl === modalEl) {
        return true
      }
      traverseEl = traverseEl.parentElement
    }
    return false
  }

  // Event handler methods

  onDragEnter(event) {
    const targetInModal = this.isElementInModal(event.target)
    if (targetInModal) {
      this.setState(() => ({ active: true }))
    }
  }

  onDragLeave(event) {
    const targetInModal = this.isElementInModal(event.target)
    const relatedInModal = this.isElementInModal(event.relatedTarget)
    if (targetInModal && !relatedInModal) {
      this.setState(() => ({ active: false }))
    }
  }

  onDragOver(event) {
    event.preventDefault() // Allow drop
  }

  onDrop(event) {
    const targetInModal = this.isElementInModal(event.target)
    if (!targetInModal) {
      return // Not handled here
    }
    event.preventDefault() // Capture drop
    const file = getFirstDroppedFile(event)
    cleanupDragData(event)

    this.setState(() => ({ active: false }))
    this.props.onDrop(file)
  }

  // Render methods

  render() {
    return tag(this.props.as, {
      ref: this.ref,
      className: classNames(
        this.props.className,
        bem.block({
          disabled: this.props.disabled,
          active: this.state.active
        })
      ),
      onClick: this.props.onClick
    },
      this.props.children
    )
  }
}

DropZone.propTypes = {
  // Values
  as: PropTypes.string.isRequired,
  children: PropTypes.any,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  // Callbacks
  onActive: PropTypes.func.isRequired,
  onClick: PropTypes.func,
  onDrop: PropTypes.func.isRequired
}

DropZone.defaultProps = {
  as: 'div',
  onActive: () => {},
  onDrop: () => {}
}

module.exports = DropZone

// DataTransfer helpers
// https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/File_drag_and_drop
// https://developer.mozilla.org/en-US/docs/Web/API/DataTransfer

function getFirstDroppedFile(event) {
  const { files, items } = event.dataTransfer
  if (items) {
    // Prefer DataTransferItemList interface
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        return items[i].getAsFile()
      }
    }
  }
  if (files.length) {
    return files[0]
  }
}

function cleanupDragData(event) {
  if (event.dataTransfer.items) {
    // Use DataTransferItemList interface to remove the drag data
    event.dataTransfer.items.clear()
  } else {
    // Use DataTransfer interface to remove the drag data
    event.dataTransfer.clearData()
  }
}
